import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * dm-addons — cross-sell cards in configurator step 3.
 *
 * A SUBSCRIBER of <dm-personalizer> state: it inherits the step-1 pet name and never
 * owns it. It publishes its own selection back through `dm:addons-change`, and the
 * personalizer pulls the final line items from getItems() at submit time.
 *
 * Contract (Design - SOPs/dm-personalized.md §Cross-sells):
 *   · Inherits the step-1 pet name, with an in-place Change name override per card.
 *   · One option drives the swatch row; the rest are pinned to the default variant.
 *   · Out of stock cannot be toggled on.
 */

const NON_LETTERS = /[^a-zA-Z]/g;

class DmAddons extends Component {
  /** @type {string} */
  #inheritedName = '';

  /**
   * The main item takes no personalization (a Plain bow), so a personalizable add-on has no
   * name to inherit. Its card steps aside rather than post a blank personalization.
   */
  #noName = false;

  /**
   * The main item's colour choices (owner 2026-10-07): a personalizable add-on picked in
   * step 3 carries the same Front Fabric and Embroidery Color as the item it was added with.
   */
  #inheritedColors = { fabricLabel: '', fabricSwatch: '', threadLabel: '', threadSwatch: '' };

  #onState = (/** @type {Event} */ event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail || typeof detail.petName !== 'string') return;
    this.#inheritedName = detail.petName;
    this.#noName = detail.noName === true;
    const text = (/** @type {unknown} */ value) => (typeof value === 'string' ? value : '');
    this.#inheritedColors = {
      fabricLabel: text(detail.toyColorLabel),
      fabricSwatch: text(detail.toyColorSwatchUrl),
      threadLabel: text(detail.embroideryLabel),
      threadSwatch: text(detail.embroideryUrl),
    };
    this.#renderAll();
  };

  /**
   * The main item's colours for this card (owner 2026-10-07: always carried over). When the
   * add-on lists the same colour (matched by handleized label) its own swatch file is used;
   * otherwise the main item's label and swatch travel as-is, so fulfilment sees exactly the
   * colour the customer chose (e.g. "Yellow Dots" on a blanket whose list says "Yellow").
   * @param {HTMLElement & any} card
   */
  #colorsFor(card) {
    const own = card.__colors || { fabrics: {}, threads: {} };
    const key = (/** @type {string} */ label) =>
      label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const { fabricLabel, fabricSwatch, threadLabel, threadSwatch } = this.#inheritedColors;
    return {
      fabric: fabricLabel
        ? { label: fabricLabel, swatch: own.fabrics?.[key(fabricLabel)] || fabricSwatch }
        : null,
      thread: threadLabel
        ? { label: threadLabel, swatch: own.threads?.[key(threadLabel)] || threadSwatch }
        : null,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    for (const card of this.#cards()) this.#initCard(card);
    document.addEventListener('dm:state', this.#onState);
    this.#renderAll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('dm:state', this.#onState);
  }

  /** @returns {HTMLElement[]} */
  #cards() {
    return Array.from(this.querySelectorAll('[data-dm-addon]'));
  }

  /** @param {HTMLElement} card */
  #initCard(card) {
    const script = card.querySelector('[data-dm-addon-variants]');
    /** @type {any[]} */
    let variants = [];
    try {
      variants = JSON.parse(script?.textContent || '[]');
    } catch (error) {
      console.error('[dm-addons] Variant payload is not valid JSON.', error);
    }

    /** @type {{ fabrics: Record<string, string>, threads: Record<string, string> }} */
    let colors = { fabrics: {}, threads: {} };
    try {
      const raw = card.querySelector('[data-dm-addon-colors]')?.textContent;
      if (raw) colors = JSON.parse(raw);
    } catch (error) {
      console.error('[dm-addons] Colour payload is not valid JSON.', error);
    }

    // Stash parsed state on the element so every handler reads one place.
    Object.assign(card, {
      __colors: colors,
      __variants: variants,
      __selected: false,
      __driveValue: '',
      __nameOverride: null,
      __variantId: card.dataset.defaultVariant || '',
    });

    const defaultVariant = variants.find((v) => String(v.id) === card.dataset.defaultVariant);
    const driveIndex = Number(card.dataset.driveIndex) || 0;
    if (defaultVariant) card.__driveValue = defaultVariant.options?.[driveIndex] || '';

    // Pre-check the drive swatch matching the default variant.
    for (const input of card.querySelectorAll('[data-dm-addon-drive]')) {
      const element = /** @type {HTMLInputElement} */ (input);
      if (element.value === card.__driveValue) element.checked = true;
      element.addEventListener('change', () => {
        card.__driveValue = element.value;
        this.#resolveVariant(card);
        this.#renderCard(card);
        this.#publish();
      });
    }

    const toggle = card.querySelector('[data-dm-addon-toggle]');
    toggle?.addEventListener('click', () => {
      if (card.dataset.soldout === 'true') return;
      card.__selected = !card.__selected;
      this.#renderCard(card);
      this.#publish();
    });

    const rename = card.querySelector('[data-dm-addon-rename]');
    const nameInput = /** @type {HTMLInputElement | null} */ (card.querySelector('[data-dm-addon-name]'));

    rename?.addEventListener('click', () => {
      if (!nameInput) return;
      const showing = !nameInput.hidden;
      nameInput.hidden = showing;
      if (!showing) {
        nameInput.value = card.__nameOverride ?? this.#inheritedName;
        nameInput.focus();
      }
    });

    nameInput?.addEventListener('input', () => {
      const sanitized = nameInput.value.replace(NON_LETTERS, '').toUpperCase();
      if (nameInput.value !== sanitized) nameInput.value = sanitized;
      // Empty override falls back to the inherited name rather than shipping a blank.
      card.__nameOverride = sanitized || null;
      this.#renderCard(card);
      this.#publish();
    });

    this.#resolveVariant(card);
  }

  /**
   * Resolve the chosen drive-option value to a concrete variant, keeping every other
   * option pinned to the default variant. Availability wins ties.
   * @param {HTMLElement & { __variants?: any[] }} card
   */
  #resolveVariant(card) {
    const variants = card.__variants || [];
    const driveIndex = Number(card.dataset.driveIndex) || 0;
    const base = variants.find((v) => String(v.id) === card.dataset.defaultVariant);
    const pinned = base?.options || [];

    const candidates = variants.filter((v) => v.options?.[driveIndex] === card.__driveValue);

    const exact = candidates.find((v) =>
      (v.options || []).every(
        (/** @type {string} */ value, /** @type {number} */ index) =>
          index === driveIndex || value === pinned[index]
      )
    );

    const chosen = exact?.available
      ? exact
      : candidates.find((v) => v.available) || exact || candidates[0] || base;

    card.__variantId = chosen ? String(chosen.id) : '';
    card.__variant = chosen || null;
  }

  #renderAll() {
    for (const card of this.#cards()) this.#renderCard(card);
    this.#publish();
  }

  /** @param {HTMLElement & any} card */
  #renderCard(card) {
    const toggle = card.querySelector('[data-dm-addon-toggle]');
    const options = card.querySelector('[data-dm-addon-options]');

    if (toggle instanceof HTMLElement) toggle.setAttribute('aria-checked', String(card.__selected));
    card.dataset.selected = String(card.__selected);
    if (options instanceof HTMLElement) {
      // data-open drives the 250ms height animation; inert keeps closed content
      // untabbable while it stays in the DOM for the transition.
      options.dataset.open = String(card.__selected);
      if (card.__selected) options.removeAttribute('inert');
      else options.setAttribute('inert', '');
    }

    const variant = card.__variant;
    const price = card.querySelector('[data-dm-addon-price]');
    if (price instanceof HTMLElement && variant?.priceFormatted) {
      price.textContent = variant.priceFormatted;
    }

    const stepAside = this.#noName && card.dataset.personalizable === 'true';
    card.hidden = stepAside;

    // Sold-out state follows the RESOLVED variant, not just the product.
    const soldOut = stepAside || card.dataset.soldout === 'true' || (variant ? variant.available === false : false);
    if (toggle instanceof HTMLButtonElement) toggle.disabled = soldOut;
    if (soldOut && card.__selected) {
      card.__selected = false;
      card.dataset.selected = 'false';
      if (options instanceof HTMLElement) {
        options.dataset.open = 'false';
        options.setAttribute('inert', '');
      }
      if (toggle instanceof HTMLElement) toggle.setAttribute('aria-checked', 'false');
    }

    const spec = card.querySelector('[data-dm-addon-spec]');
    if (spec instanceof HTMLElement) {
      const parts = [];
      if (card.dataset.personalizable === 'true') {
        const name = card.__nameOverride ?? this.#inheritedName;
        if (name) parts.push(`Embroidered “${name}”`);
        const { fabric, thread } = this.#colorsFor(card);
        if (thread) parts.push(thread.label);
        if (fabric) parts.push(fabric.label);
      }
      if (card.__driveValue) parts.push(card.__driveValue);
      spec.textContent = parts.join(' · ');
    }
  }

  /**
   * Line items for the toggled cards, ready for /cart/add.js.
   * @param {string} parentName
   */
  getItems(parentName) {
    /** @type {any[]} */
    const items = [];

    for (const card of this.#cards()) {
      const element = /** @type {HTMLElement & any} */ (card);
      if (!element.__selected || !element.__variantId) continue;

      /** @type {Record<string, string>} */
      const properties = { _dm_addon_for: parentName || '' };

      if (element.dataset.personalizable === 'true') {
        const name = element.__nameOverride ?? this.#inheritedName;
        if (name) properties['Pet Name'] = name;
        // Same keys the configurator posts for the main item.
        const { fabric, thread } = this.#colorsFor(element);
        if (thread) {
          properties['Embroidery Color'] = thread.label;
          if (thread.swatch) properties._wroe_embroidery_color_swatch = thread.swatch;
        }
        if (fabric) {
          properties['Front Fabric'] = fabric.label;
          if (fabric.swatch) properties._wroe_front_fabric_swatch = fabric.swatch;
        }
      }

      items.push({
        id: Number(element.__variantId),
        quantity: 1,
        properties,
        // Carried for error reporting, stripped before the request.
        _title: element.dataset.productTitle || 'Add-on',
        _cardId: element.dataset.productId || '',
      });
    }

    return items;
  }

  /** Total of the toggled cards, in cents. */
  getTotalCents() {
    let total = 0;
    for (const card of this.#cards()) {
      const element = /** @type {HTMLElement & any} */ (card);
      if (element.__selected && element.__variant) total += Number(element.__variant.price) || 0;
    }
    return total;
  }

  /**
   * Turn a card off after the cart rejected it, and say why.
   * @param {string} productId
   * @param {string} message
   */
  rejectCard(productId, message) {
    for (const card of this.#cards()) {
      const element = /** @type {HTMLElement & any} */ (card);
      if (element.dataset.productId !== productId) continue;

      element.__selected = false;
      element.dataset.soldout = 'true';
      this.#renderCard(element);

      const error = element.querySelector('[data-dm-addon-error]');
      if (error instanceof HTMLElement) {
        error.hidden = false;
        error.textContent = message;
      }
    }
    this.#publish();
  }

  #publish() {
    this.dispatchEvent(
      new CustomEvent('dm:addons-change', {
        detail: { totalCents: this.getTotalCents(), count: this.getItems('').length },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** @param {number} cents */
  money(cents) {
    try {
      return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
    } catch {
      return '';
    }
  }
}

if (!customElements.get('dm-addons')) {
  customElements.define('dm-addons', DmAddons);
}
